(function () {
  if (!customElements.get('media-gallery')) {
    const _isDev =
      window.location.hostname === 'localhost' ||
      new URLSearchParams(window.location.search).has('preview_theme_id');

    customElements.define(
      'media-gallery',
      class MediaGallery extends HTMLElement {
        constructor() {
          super();

          this.selectors = {
            viewer: '[id^="GalleryViewer"]',
            thumbnails: '[id^="GalleryThumbnails"]',
            mediaList: '[id^="Slider-Gallery"]',
            mediaItems: ['.product__media-item'],
          };

          this.sliderInstance = false;
          this.thumbsInstance = false;
          this.slideHeights = new Map(); // Store pre-calculated heights for smooth transitions
          this.imageLoadListeners = new Map(); // Track image load listeners for cleanup
          this.swiperEventHandlers = []; // Track Swiper event handlers for cleanup
        }

        connectedCallback() {
          this.init();
        }

        init() {
          this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
          this.mediaLayout = this.dataset.mediaLayout;
          this.onlyImage = this.dataset.onlyImage === 'true';
          this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
          this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
          this.enableImageZoom = this.dataset.enableImageZoom === 'true';
          this.setSliderOptions();

          this.mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          this.boundUpdateMediaLayout = this.updateMediaLayout.bind(this);
          this.mql.addEventListener('change', this.boundUpdateMediaLayout);
          this.updateMediaLayout();

          if (this.enableImageZoom) {
            this.initImageZoom();
          }
        }

        disconnectedCallback() {
          if (this.mql && this.boundUpdateMediaLayout) {
            this.mql.removeEventListener('change', this.boundUpdateMediaLayout);
          }

          if (this.lightbox) {
            this.lightbox.destroy();
            this.lightbox = null;
          }

          if (this._zoomClickHandler) {
            this.removeEventListener('click', this._zoomClickHandler, true);
            this._zoomClickHandler = null;
          }

          // Clean up image load listeners to prevent memory leaks
          if (this.imageLoadListeners && this.imageLoadListeners.size > 0) {
            this.imageLoadListeners.forEach((handler, img) => {
              img.removeEventListener('load', handler);
            });
            this.imageLoadListeners.clear();
          }

          // Clean up Swiper event handlers to prevent memory leaks
          if (
            this.sliderInstance &&
            typeof this.sliderInstance === 'object' &&
            this.swiperEventHandlers.length > 0
          ) {
            this.swiperEventHandlers.forEach(({ event, handler }) => {
              this.sliderInstance.slider.off(event, handler);
            });
            this.swiperEventHandlers = [];
          }

          // Clear slide heights Map to free memory
          if (this.slideHeights) {
            this.slideHeights.clear();
          }

          if (this.thumbsInstance && typeof this.thumbsInstance === 'object') {
            this.thumbsInstance.slider.destroy();
            this.thumbsInstance = false;
          }

          if (this.sliderInstance && typeof this.sliderInstance === 'object') {
            this.sliderInstance.slider.destroy();
            this.sliderInstance = false;
          }
        }

        setSliderOptions() {
          const mediaItemGap = parseInt(this.dataset.mediaItemGap, 10);

          // iOS detection - ALL iOS browsers use Safari's WebKit engine (Apple requirement)
          // Chrome, Firefox, Edge on iOS are all WebKit with different UI, same GPU limits
          // Loop mode causes DOM explosion (30 slides) exceeding mobile GPU limits
          const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

          const isMobileSafari = isIOS; // All iOS browsers are effectively mobile Safari (WebKit)
          // Samsung Internet misidentifies passive listener requirements, causing touchmove suppression
          const isSamsungBrowser = /SamsungBrowser/.test(navigator.userAgent);

          this.sliderOptions = {
            init: false,
            slidesPerView: '1',
            spaceBetween: mediaItemGap,
            loop: !isIOS, // Disable loop on ALL iOS browsers - prevents 30 slide DOM explosion
            grabCursor: !isMobileSafari,
            allowTouchMove: true,
            autoHeight: false, // Disable Swiper autoHeight - conflicts with custom height system
            navigation: {
              nextEl: this.querySelector('.swiper-button-next'),
              prevEl: this.querySelector('.swiper-button-prev'),
            },
            pagination: {
              el: this.querySelector('.swiper-pagination'),
              clickable: true,
              type: 'fraction',
            },
            threshold: 2,

            // iOS-specific optimizations (all iOS browsers use WebKit)
            ...(isIOS && {
              observer: true,
              observeParents: true,
              observeSlideChildren: false,
              resistanceRatio: 0.85,
              longSwipesRatio: 0.3,
              watchSlidesProgress: false,
            }),

            // Samsung Internet: registers touch handlers as passive by default,
            // which causes the browser to suppress touchmove and break swiping
            ...(isSamsungBrowser && {
              touchEventsTarget: 'wrapper',
              passiveListeners: false,
              threshold: 5,
            }),
          };

          this.thumbsOptions = {
            slidesPerView: 4,
            breakpoints: {
              461: {
                slidesPerView: 5,
              },
            },
            spaceBetween: mediaItemGap,
            loop: false,
            freeMode: true,
            watchSlidesProgress: true,
            threshold: 2,
          };

          switch (this.mediaLayout) {
            case 'vertical-carousel':
              this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
                breakpoints: {
                  768: {
                    direction: 'vertical',
                    slidesPerView: 'auto',
                  },
                },
              });
              break;
            case 'slider-freemode':
              this.sliderOptions = Object.assign({}, this.sliderOptions, {
                slidesPerView: 'auto',
              });
              break;
          }
        }

        updateMediaLayout() {
          if (FoxTheme.config.mqlMobile) {
            this.initSlider();
          } else {
            if (this.enableDesktopSlider) {
              this.initSlider();
            } else {
              this.destroySlider();
            }
          }
        }

        initSlider() {
          if (typeof this.sliderInstance !== 'object') {
            if (
              (this.enableDesktopSlider || this.enableMobileThumbnails) &&
              this.elements.thumbnails
            ) {
              this.thumbsInstance = new window.FoxTheme.Carousel(
                this.elements.thumbnails,
                this.thumbsOptions
              );
              this.thumbsInstance.init();

              this.sliderOptions.thumbs = {
                swiper: this.thumbsInstance.slider,
                autoScrollOffset: 2,
              };
            }

            this.sliderInstance = new window.FoxTheme.Carousel(
              this.elements.viewer,
              this.sliderOptions,
              [FoxTheme.Swiper.Thumbs]
            );
            this.sliderInstance.init();

            this.handleSliderAfterInit();
            this.handleSlideChange();

            this.sliderInstance.slider.init();
          }
        }

        destroySlider() {
          if (typeof this.sliderInstance === 'object') {
            this.sliderInstance.slider.destroy();
            this.sliderInstance = false;
          }
        }

        initThumbsSlider() {
          if (typeof this.thumbsInstance !== 'object') {
            this.thumbsInstance = new window.FoxTheme.Carousel(
              this.selectors.thumbnails,
              this.thumbsOptions
            );
            this.thumbsInstance.init();
          }
        }

        initImageZoom() {
          const dataSource = [];
          const allMedia = [
            ...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)'),
          ];
          if (allMedia) {
            allMedia.forEach((media) => {
              switch (media.dataset.mediaType) {
                case 'model':
                  dataSource.push({
                    id: media.dataset.mediaIndex,
                    html: `<div class="pswp__item--${media.dataset.mediaType}">${
                      media.querySelector('product-model').outerHTML
                    }</div>`,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
                case 'video':
                case 'external_video':
                  dataSource.push({
                    id: media.dataset.mediaIndex,
                    html: `<div class="pswp__item--${media.dataset.mediaType}">${
                      media.querySelector('video-element').outerHTML
                    }</div>`,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
                case 'image':
                  dataSource.push({
                    id: media.dataset.mediaIndex,
                    src: media.dataset.src,
                    width: media.dataset.pswpWidth,
                    height: media.dataset.pswpHeight,
                    mediaType: media.dataset.mediaType,
                  });
                  break;
              }
            });
          }

          this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
            dataSource: dataSource,
            pswpModule: window.FoxTheme.PhotoSwipe,
            bgOpacity: 1,
            arrowPrev: false,
            arrowNext: false,
            zoom: false,
            close: false,
            counter: false,
            preloader: false,
          });

          this.lightbox.addFilter('thumbEl', (thumbEl, { id: _id }, _index) => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              const { slides, activeIndex } = this.sliderInstance.slider;
              if (slides && slides[activeIndex]) {
                const el = slides[activeIndex].querySelector('img');
                if (el) {
                  return el;
                }
              }
            }

            return thumbEl;
          });

          this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id: _id } }) => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              const { slides, activeIndex } = this.sliderInstance.slider;
              if (slides[activeIndex]) {
                const el = slides[activeIndex].querySelector('img');
                if (el) {
                  return el.src;
                }
              }
            }

            return placeholderSrc;
          });

          // Sync slider position when lightbox closes (not during navigation)
          // Using closingAnimationStart prevents constant syncing during swiping
          this.lightbox.on('closingAnimationStart', () => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              const { currIndex } = this.lightbox.pswp;
              // Use slideTo() instead of slideToLoop() - more reliable, fewer bugs
              // See: https://github.com/nolimits4web/swiper/issues/2942
              this.sliderInstance.slider.slideTo(currIndex, 0, false);
            }
          });

          // Pause media during lightbox navigation
          this.lightbox.on('change', () => {
            window.pauseAllMedia(this);
          });

          this.lightbox.on('pointerDown', (e) => {
            if (this.lightbox.pswp.currSlide.data.mediaType !== 'image') {
              e.preventDefault();
            }
          });

          this.lightbox.on('uiRegister', () => {
            if (!this.onlyImage) {
              this.lightbox.pswp.ui.registerElement({
                name: 'next',
                ariaLabel: 'Next slide',
                order: 3,
                isButton: true,
                html: '<svg class="pswp-icon-next" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" transform="translate(100, 100) rotate(180) "></path></svg>',
                onClick: (_event, _el) => {
                  this.lightbox.pswp.next();
                },
              });
              this.lightbox.pswp.ui.registerElement({
                name: 'prev',
                ariaLabel: 'Previous slide',
                order: 1,
                isButton: true,
                html: '<svg class="pswp-icon-prev" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
                onClick: (_event, _el) => {
                  this.lightbox.pswp.prev();
                },
              });
            }
            this.lightbox.pswp.ui.registerElement({
              name: 'close-zoom',
              ariaLabel: 'Close zoom image',
              order: 2,
              isButton: true,
              html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
              onClick: (_event, _el) => {
                this.lightbox.pswp.close();
              },
            });
          });

          this.lightbox.init();

          // Zoom click handler - handles opening PhotoSwipe lightbox
          // Use direct event listener with capture phase to ensure we catch clicks
          //
          // IMPORTANT: DOM Structure (from product-thumbnail.liquid):
          //   .product__media-item
          //     ├── motion-element > img  ← User clicks here
          //     ├── .product__media-icon  ← Decorative zoom icon (pointer-events: none)
          //     └── button.js-photoswipe--zoom  ← Actual clickable button (sibling, not parent!)
          //
          // Because the image and zoom button are SIBLINGS (not parent-child),
          // we must find the zoom button via querySelector, not closest().
          //
          const handleZoomClick = (e) => {
            // Check if clicked on zoom button OR on the image/media item itself
            const button = e.target.closest('.js-photoswipe--zoom');
            const mediaItem = e.target.closest('.product__media-item');

            // If no media item found, ignore click
            if (!mediaItem) return;

            // For image clicks, find the sibling zoom button
            const zoomButton = button || mediaItem.querySelector('.js-photoswipe--zoom');
            if (!zoomButton) return;

            // Only handle image type media
            const mediaType = zoomButton.dataset.mediaType || mediaItem.dataset.mediaType;
            if (mediaType !== 'image') return;

            e.preventDefault();
            e.stopPropagation();

            let index;
            if (
              this.sliderInstance &&
              this.sliderInstance.slider &&
              this.sliderInstance.slider.realIndex !== undefined
            ) {
              index = this.sliderInstance.slider.realIndex;
            } else {
              index = Number(zoomButton.dataset.mediaIndex || mediaItem.dataset.mediaIndex) || 0;
            }

            this.lightbox.loadAndOpen(index);
          };

          // Remove any existing handler before adding new one
          if (this._zoomClickHandler) {
            this.removeEventListener('click', this._zoomClickHandler, true);
          }

          // Use capture phase to ensure we catch clicks before other handlers
          this.addEventListener('click', handleZoomClick, true);
          this._zoomClickHandler = handleZoomClick;
        }

        handleSliderAfterInit() {
          // Create bound handler for cleanup
          this.afterInitHandler = (swiper) => {
            const { slides, activeIndex } = swiper;

            if (slides[activeIndex]) {
              const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
              this.toggleSliderDraggableState(!isModelMediaType);
            }

            // Pre-calculate slide heights for smooth dynamic sizing
            this.calculateSlideHeights();

            // Set initial height
            this.updateGalleryHeight(activeIndex);
          };

          // Attach handler and track for cleanup
          this.sliderInstance.slider.on('afterInit', this.afterInitHandler);
          this.swiperEventHandlers.push({ event: 'afterInit', handler: this.afterInitHandler });
        }

        calculateSlideHeights() {
          // Pre-calculate all slide heights in a single batch (prevents reflow storms)
          const containerWidth = this.elements.viewer.offsetWidth;

          // Height cap to prevent GPU memory exhaustion on iPhone 16
          // Calculation: 10 slides × (1.2 × viewport height) = safe GPU usage
          // Without cap: 10 slides × 2500px = 25,000px > 20,000px GPU limit ❌
          // With cap: 10 slides × (1.2 × ~900px iPhone) = ~11,000px < 20,000px ✓
          const maxHeight = window.innerHeight * 1.2;

          this.sliderInstance.slider.slides.forEach((slide, index) => {
            const img = slide.querySelector('img');
            if (img) {
              // Use async decode API to prevent GPU memory spikes on main thread
              if (img.decode && img.complete) {
                img.decode().catch((e) => {
                  if (_isDev) console.error('[MediaGallery] img.decode() failed:', e);
                  // Fallback: Sync decode will happen on render if async fails
                });
              }

              // Check if image is already loaded
              if (img.complete && img.naturalWidth && img.naturalHeight) {
                // Image loaded - calculate natural height based on container width
                const aspectRatio = img.naturalHeight / img.naturalWidth;
                const naturalHeight = containerWidth * aspectRatio;
                // Cap height to prevent GPU memory exhaustion while preserving aspect ratio
                const cappedHeight = Math.min(naturalHeight, maxHeight);
                this.slideHeights.set(index, cappedHeight);
              } else {
                // Image not loaded yet - estimate height from the image's known dimensions
                // (data-pswp-width / data-pswp-height are set in product-thumbnail.liquid
                // from media.preview_image dimensions, so we get the correct aspect ratio
                // without waiting for the image to load, preventing the scroll jump)
                const pswpW = parseInt(slide.dataset.pswpWidth, 10);
                const pswpH = parseInt(slide.dataset.pswpHeight, 10);
                const estimatedHeight =
                  pswpW && pswpH
                    ? Math.min(containerWidth * (pswpH / pswpW), maxHeight)
                    : containerWidth;
                this.slideHeights.set(index, estimatedHeight);

                const loadHandler = () => {
                  if (img.naturalWidth && img.naturalHeight) {
                    const aspectRatio = img.naturalHeight / img.naturalWidth;
                    const naturalHeight = containerWidth * aspectRatio;
                    // Cap height to prevent GPU memory exhaustion
                    const cappedHeight = Math.min(naturalHeight, maxHeight);
                    this.slideHeights.set(index, cappedHeight);

                    // Update height if this is the active slide
                    if (index === this.sliderInstance.slider.activeIndex) {
                      this.updateGalleryHeight(index);
                    }
                  }
                  // Remove from tracking after it fires (since it's { once: true })
                  this.imageLoadListeners.delete(img);
                };

                img.addEventListener('load', loadHandler, { once: true });
                // Track listener for cleanup in case component is destroyed before load
                this.imageLoadListeners.set(img, loadHandler);
              }
            } else {
              // No image found - use container width as fallback
              this.slideHeights.set(index, containerWidth);
            }
          });
        }

        updateGalleryHeight(activeIndex) {
          // Smoothly transition to the active slide's pre-calculated height
          const height = this.slideHeights.get(activeIndex);
          if (height && this.elements.viewer) {
            this.elements.viewer.style.transition = 'height 0.3s ease';
            this.elements.viewer.style.height = `${height}px`;
          }
        }

        handleSlideChange() {
          // Create bound handler for cleanup
          this.realIndexChangeHandler = (swiper) => {
            const { slides, activeIndex, thumbs } = swiper;

            if (thumbs.swiper) {
              thumbs.swiper.slideTo(activeIndex);
            }

            if (slides[activeIndex]) {
              this.playActiveMedia(slides[activeIndex]);

              const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
              this.toggleSliderDraggableState(!isModelMediaType);

              // Update gallery height smoothly for the new slide
              this.updateGalleryHeight(activeIndex);
            }
          };

          // Attach handler and track for cleanup
          this.sliderInstance.slider.on('realIndexChange', this.realIndexChangeHandler);
          this.swiperEventHandlers.push({
            event: 'realIndexChange',
            handler: this.realIndexChangeHandler,
          });
        }

        toggleSliderDraggableState(isDraggable) {
          if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
            this.sliderInstance.slider.allowTouchMove = isDraggable;
          }
        }

        playActiveMedia(selected) {
          const deferredMedia = selected.querySelector('product-model');
          if (deferredMedia) deferredMedia.loadContent(false);
        }

        setActiveMedia(variant) {
          // Early exit if variant has no featured media (some variants don't have images)
          if (
            !variant ||
            !Object.prototype.hasOwnProperty.call(variant, 'featured_media') ||
            !variant.featured_media
          ) {
            return;
          }

          // Check if slider is initialized and ready (both instance and .slider must exist)
          if (
            this.sliderInstance &&
            typeof this.sliderInstance === 'object' &&
            this.sliderInstance.slider
          ) {
            /**
             * ⚠️ CRITICAL: ALWAYS find slides by media ID, NEVER by position index
             *
             * WHY THIS MATTERS:
             * Shopify's featured_media.position does NOT correspond to Swiper slide indices!
             * - Shopify's position is relative to the variant's own media list (1, 2, 3...)
             * - Swiper slides contain ALL product media in the order they appear in HTML
             * - These two orderings can be completely different!
             *
             * HISTORICAL BUG (Jan 2026):
             * ❌ WRONG CODE (caused variants to show incorrect images):
             *    const slideIndex = variant.featured_media.position - 1;
             *    this.sliderInstance.slider.slideTo(slideIndex, 0, false);
             *
             * Result: Variant 1 showed wrong image, Variant 2 showed wrong image, etc.
             * because position 1 != slide index 0, position 2 != slide index 1
             *
             * ✅ CORRECT APPROACH:
             * 1. Loop through ALL slides in the Swiper instance
             * 2. Find the slide where data-media-id matches variant.featured_media.id
             * 3. Use that slide's actual array index for slideTo()
             *
             * EXAMPLE FROM LIVE PRODUCT:
             * Variant 1: featured_media.id = 36345138872540, position = 1
             *   → Found at slides[1] (not slides[0]!)
             * Variant 3: featured_media.id = 36345138970844, position = 2
             *   → Found at slides[2] (happens to match, but not guaranteed!)
             *
             * DO NOT CHANGE THIS TO USE POSITION - IT WILL BREAK IMAGE SWITCHING
             */
            let slideIndex = -1;

            // Search for slide with matching media ID
            const slides = this.sliderInstance.slider.slides;
            for (let i = 0; i < slides.length; i++) {
              if (
                slides[i].dataset &&
                slides[i].dataset.mediaId === String(variant.featured_media.id)
              ) {
                slideIndex = i;
                break;
              }
            }

            // Fallback: If no match found (shouldn't happen), use position with 1-to-0 index conversion
            // This fallback exists for safety but should rarely execute
            if (slideIndex === -1) {
              slideIndex = Math.max(0, (variant.featured_media.position || 1) - 1);
            }

            // Use slideTo() instead of slideToLoop() - compatible with Safari (loop disabled)
            this.sliderInstance.slider.slideTo(slideIndex, 0, false);
          } else {
            this.sortMediaItems(variant);
          }
        }

        sortMediaItems(variant) {
          /**
           * FALLBACK METHOD: Used when slider is not yet initialized
           * This reorders DOM elements directly instead of using slideTo()
           *
           * NOTE: This also uses media ID matching (not position) - same principle as setActiveMedia()
           * The media with matching variant.featured_media.id gets moved to the front of the list
           */
          const newMedias = this.elements.mediaItems;

          // Reset to original ordering first
          newMedias.sort(function (a, b) {
            return a.dataset.mediaIndex - b.dataset.mediaIndex;
          });

          // Find media by ID (NOT position) and move to front
          newMedias.some((media, index) => {
            if (media.dataset.mediaId === String(variant.featured_media.id)) {
              const [element] = newMedias.splice(index, 1);
              newMedias.unshift(element);
              return true;
            }
          });

          this.elements.mediaList.textContent = '';
          newMedias.forEach((media) => {
            this.elements.mediaList.appendChild(media);
          });

          if (!FoxTheme.config.mqlMobile) {
            const selectedMedia = this.querySelector(
              `[data-media-id="${variant.featured_media.id}"]`
            );
            if (selectedMedia) {
              window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
            }
          }
        }
      }
    );
  }
})();
